import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { CSSObject } from '@mui/system';
import ColorConstants from '../../defaults/ColorConstants';
import store from '../../redux/store';

// ATTN: This value will only be read from the store ONCE - at build.
// 'headerHeight' is never actually set again, so in this scenario it
// works. Eventually 'headerHeight' should only be a CSS variable with
// conditional classes applied if it ever needs to change.
const headerHeight = store.getState().displayState.headerHeight;

export const createOpenedMixin = (drawerWidth: number) => (theme: Theme): CSSObject => ({
    width: drawerWidth,
    top: headerHeight + 'px',
    left: 'auto',
    backgroundColor: ColorConstants.AvatarBackgroundColor,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    overflowY: 'hidden',
});

export const createClosedMixin = () => (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    top: headerHeight + 'px',
    left: 'auto',
    backgroundColor: ColorConstants.AvatarBackgroundColor,
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 1),
    height: '48px !important',
    backgroundColor: ColorConstants.WhiteAlien,
    ...theme.mixins.toolbar,
}));

export const createStyledDrawer = (drawerWidth: number) => {
    const openedMixin = createOpenedMixin(drawerWidth);
    const closedMixin = createClosedMixin();

    return styled(MuiDrawer, {
        shouldForwardProp: (prop) => prop !== 'open',
    })(({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }));
};